import React, { ReactNode } from "react";
import { BookHalf } from "react-bootstrap-icons";
import { Button, Col, Container, Row } from "react-bootstrap";

import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { DocsSidebar, WidgetCard } from "../../../components/analytics";
import { DocsSidebarEntry, WidgetTheme } from "../../../types/analytics";
import { getWidgetTheme, getDefaultDateRange } from "../../../utils/analytics";
import { getAnalyticsWidget } from "../../../utils/Requests";

type ExternalLandingProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type ExternalLandingState = {
    isLoading: boolean;
    error: string | null;
    isDarkTheme: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
    topLineMetrics: MetricCardItem[];
    recsMetrics: MetricCardItem[];
    kpiDescription: string | null;
    recsDescription: string | null;
};

class ExternalLanding extends React.Component<ExternalLandingProps, ExternalLandingState> {
    private getSchemaDescription = (schema: Record<string, { description?: string }> | undefined, keys: string[]): string | undefined => {
        for (const key of keys) {
            const description = schema?.[key]?.description;
            if (description) {
                return description;
            }
        }
        return undefined;
    };

    constructor(props: ExternalLandingProps) {
        super(props);
        this.state = {
            isLoading: true,
            error: null,
            isDarkTheme: true,
            sidebarOpen: false,
            sidebarEntries: [],
            topLineMetrics: [],
            recsMetrics: [],
            kpiDescription: null,
            recsDescription: null
        };
    }

    private toggleSidebar = (): void => {
        this.setState((prevState) => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    public componentDidMount(): void {
        this.fetchTopLineMetrics();
    }

    private fetchTopLineMetrics = async (): Promise<void> => {
        const { startDate, endDate } = getDefaultDateRange();

        try {
            this.setState({ isLoading: true, error: null });

            const [trafficResponse, userKpiResponse, recsResponse] = await Promise.all([
                getAnalyticsWidget(this.props.customerConfig, "kpi_traffic", startDate, endDate, [], [], []),
                getAnalyticsWidget(this.props.customerConfig, "kpi_user_kpi", startDate, endDate, [], [], []),
                getAnalyticsWidget(this.props.customerConfig, "recs_plays", startDate, endDate, [], [], [])
            ]);

            const trafficRow = Array.isArray((trafficResponse as any)?.result) ? (trafficResponse as any).result[0] : null;
            const userKpiRow = Array.isArray((userKpiResponse as any)?.result) ? (userKpiResponse as any).result[0] : null;
            const trafficSchema = (trafficResponse as any)?.schema as Record<string, { description?: string }> | undefined;
            const userKpiSchema = (userKpiResponse as any)?.schema as Record<string, { description?: string }> | undefined;
            const recsSchema = (recsResponse as any)?.schema as Record<string, { description?: string }> | undefined;
            const kpiDescription = (trafficResponse as any)?.widget?.description
                || (userKpiResponse as any)?.widget?.description
                || null;
            const recsDescription = (recsResponse as any)?.widget?.description || null;
            const sidebarEntries: DocsSidebarEntry[] = [trafficResponse, userKpiResponse, recsResponse]
                .map((response: any, index: number) => {
                    const meta = response?.widget;
                    if (!meta) return null;

                    const widgetKeys = ["kpi_traffic", "kpi_user_kpi", "recs_plays"];

                    /* eslint-disable camelcase */
                    return {
                        widgetKey: widgetKeys[index],
                        displayName: meta.display_name,
                        description: meta.description,
                        schema: response?.schema
                    } as DocsSidebarEntry;
                    /* eslint-enable camelcase */
                })
                .filter((entry): entry is DocsSidebarEntry => entry !== null);

            const topLineMetrics: MetricCardItem[] = [
                {
                    key: "session_count",
                    label: "Sessions",
                    value: trafficRow?.session_count ?? null,
                    changePercent: trafficRow?.session_percent_change ?? null,
                    description: this.getSchemaDescription(trafficSchema, ["session_count"])
                },
                {
                    key: "play_count",
                    label: "Video Starts",
                    value: trafficRow?.play_count ?? null,
                    changePercent: trafficRow?.plays_percent_change ?? null,
                    description: this.getSchemaDescription(trafficSchema, ["play_count", "plays"])
                },
                {
                    key: "user_count",
                    label: "Users",
                    value: userKpiRow?.user_count ?? null,
                    changePercent: userKpiRow?.user_percent_change ?? null,
                    description: this.getSchemaDescription(userKpiSchema, ["user_count", "users"])
                },
                {
                    key: "playing_user_count",
                    label: "Playing Users",
                    value: userKpiRow?.playing_user_count ?? null,
                    changePercent: userKpiRow?.playing_user_percent_change ?? null,
                    description: this.getSchemaDescription(userKpiSchema, ["playing_user_count"])
                },
                {
                    key: "viewing_user_count",
                    label: "Viewing Users",
                    value: userKpiRow?.viewing_user_count ?? null,
                    changePercent: userKpiRow?.viewing_user_percent_change ?? null,
                    description: this.getSchemaDescription(userKpiSchema, ["viewing_user_count"])
                },
                {
                    key: "device_count",
                    label: "Devices",
                    value: userKpiRow?.device_count ?? null,
                    changePercent: userKpiRow?.device_percent_change ?? null,
                    description: this.getSchemaDescription(userKpiSchema, ["device_count"])
                }
            ];

            const recsRowsRaw = Array.isArray((recsResponse as any)?.result) ? (recsResponse as any).result : [];
            const allRecsRows = recsRowsRaw
                .map((row: Record<string, any>) => {
                    const ruleId = String(row.parameters_rule_id || row.parameter_rule_id || "").trim();
                    const source = (!ruleId || ruleId.toLowerCase() === "unknown" || ruleId.toLowerCase() === "null")
                        ? "All recommendations"
                        : ruleId;
                    return {
                        source,
                        plays: Number(row.plays || 0),
                        users: Number(row.users || 0),
                        views: Number(row.views || row.view_count || 0)
                    };
                })
                .filter((row: Record<string, any>) => row.source === "All recommendations");

            const recTotals = allRecsRows.reduce((acc: { plays: number; users: number; views: number }, row: Record<string, any>) => {
                acc.plays += Number(row.plays || 0);
                acc.users += Number(row.users || 0);
                acc.views += Number(row.views || 0);
                return acc;
            }, { plays: 0, users: 0, views: 0 });

            const recsMetrics: MetricCardItem[] = [
                {
                    key: "recs_plays",
                    label: "Recommendation Plays",
                    value: recTotals.plays,
                    description: this.getSchemaDescription(recsSchema, ["plays", "rec_plays"])
                },
                {
                    key: "recs_users",
                    label: "Recommendation Users",
                    value: recTotals.users,
                    description: this.getSchemaDescription(recsSchema, ["users", "rec_users"])
                },
                {
                    key: "recs_views",
                    label: "Recommendation Views",
                    value: recTotals.views,
                    description: this.getSchemaDescription(recsSchema, ["views", "view_count", "rec_views"])
                }
            ];

            this.setState({ topLineMetrics, recsMetrics, kpiDescription, recsDescription, sidebarEntries, isLoading: false });
        } catch (_error) {
            this.setState({ error: "Failed to load external landing metrics", isLoading: false });
        }
    };

    public render(): ReactNode {
        const { isDarkTheme, topLineMetrics, recsMetrics, isLoading, error, kpiDescription, recsDescription, sidebarOpen, sidebarEntries } = this.state;
        const theme: WidgetTheme = getWidgetTheme(isDarkTheme);

        return (
            <>
                <div style={{ display: "flex", alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Container className="mw-100" style={{ paddingBottom: "40px", position: "relative" }}>
                            <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                                <Button
                                    onClick={this.toggleSidebar}
                                    style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px" }}
                                    title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                                >
                                    <BookHalf />
                                </Button>
                            </div>
                            <Row style={{ marginBottom: "16px" }}>
                                <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                                    <h2 style={{ margin: 0 }}>Analytics - Overview</h2>
                                </Col>
                            </Row>
                            <WidgetCard
                                title="KPI over last 7 days"
                                titleAdornment={(
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        href="/analytics/external/kpi-dashboard"
                                    >
                                        Open KPI dashboard
                                    </Button>
                                )}
                                description={kpiDescription || undefined}
                                theme={theme}
                                isLoading={isLoading}
                                error={error}
                                isEmpty={!isLoading && !error && topLineMetrics.length === 0}
                                emptyMessage="No KPI top line data available."
                            >
                                <MetricCards
                                    metrics={topLineMetrics}
                                    theme={theme}
                                    emptyMessage="No KPI top line data available."
                                    minCardWidth="200px"
                                    maxCardWidth="220px"
                                    gridGap="16px"
                                />
                            </WidgetCard>
                            <WidgetCard
                                title="Recommendation performance"
                                titleAdornment={(
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        href="/analytics/external/recs-dashboard"
                                    >
                                        Open recs dashboard
                                    </Button>
                                )}
                                description={recsDescription || undefined}
                                theme={theme}
                                isLoading={isLoading}
                                error={error}
                                isEmpty={!isLoading && !error && recsMetrics.length === 0}
                                emptyMessage="No recommendation performance data available."
                            >
                                <MetricCards
                                    metrics={recsMetrics}
                                    theme={theme}
                                    emptyMessage="No recommendation performance data available."
                                    minCardWidth="220px"
                                    maxCardWidth="260px"
                                    gridGap="16px"
                                />
                            </WidgetCard>
                        </Container>
                    </div>
                    <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
                </div>

            </>
        );
    }
}

export default ExternalLanding;